import { GlobalState } from '../../../../store/global';
import { createSelector } from '@ngrx/store';
import { NoteColor } from '../../enums/note-color';
import { canCreateGbpConnection, getPrimaryFieldId, hasBlacklistedFields, hasDifferences, isLocationFieldBlacklisted } from '../../location-utils';
import { ALTERNATIVE_FIELD_IDS, BUSINESS_HOURS_SUBFIELDS, COMPLEX_DATA_CONNECTION_FIELDS } from '../../constants';
import { DataSourceStatus } from '../../enums/data-source-status';
import { isComplexDataConnectionField } from '../../data-source-utils';
import { addressTranslationsAdapter, columnNotesAdapter, locationsAdapter } from './location-list-adapters';
import { LSCField } from '../../../../models/LSCFields';
import { OngoingActionsEnum } from '../../enums/ongoing-actions';
import { Location } from '../../../../models/locations-models/location';
import { CCSyncStatus } from '../../../../models/locations-models/cc-sync-details';
import { isAfter, isBefore, subHours } from 'date-fns';

export const state = (state: GlobalState) => state.locations.locationList;

export const selectCountryList = createSelector(state, (locations) => locations.countryISO2List);
export const selectGbpLocationGroups = createSelector(state, (locations) => locations.gbpLocationGroups);
export const selectDatasourceSyncDetails = createSelector((state), (locations) => locations.datasourceSyncDetails);
export const selectCCSyncDetails = createSelector((state), (locations) => locations.ccSyncDetails);
export const selectLastSuccessfulPageSize = createSelector((state), (locations) => locations.lastSuccessfulPageSize);
export const selectValidationHistory = createSelector(state, (state) => state.validationHistory);
export const selectBulkValidationDetails = createSelector(state, (state) => state.bulkValidationDetails);

/***************************************************************************************************
 *                                          LOCATIONS                                              *
 **************************************************************************************************/

export const locationsSelectors = locationsAdapter.getSelectors();

export const selectLocationsState = createSelector(state, (locationList) => locationList.locations);


export const selectLocations = createSelector(selectLocationsState, locationsSelectors.selectAll); // array

export const selectLocationsEntities = createSelector(selectLocationsState, locationsSelectors.selectEntities); // dictionary

export const selectLocation = (id: number) => createSelector(selectLocationsEntities, (locations) => locations[id]);


/***************************************************************************************************
 *                              LOCATION STATUS SELECTORS
 **************************************************************************************************/


/**
 * Checks if a location has ignored/accepted differences to its GMB location.
 * @param locationId
 */
export const selectHasIgnoredDifferences = (locationId: number) => createSelector(selectLocation(locationId),
    (location) => hasDifferences(location, 'ignored'));

/**
 * Checks if the location has differences that are not handled (not ignored/accepted) yet.
 * @param locationId
 */
export const selectHasOpenDifferences = (locationId: number) => createSelector(selectLocation(locationId),
    (location) => hasDifferences(location, 'open'));

/**
 * Checks if a location has blacklisted fields.
 * @param locationId
 */
export const selectHasBlacklistedFields = (locationId: number) => createSelector(selectLocation(locationId),
    (location) => {
      return hasBlacklistedFields(location);
    });

/**
 * Determines 'blacklist' status for a given field
 * @param locationId
 * @param fieldId
 */
export const selectIsLocationFieldBlacklisted = (locationId: number, fieldId: LSCField) => createSelector(
    selectLocation(locationId), (location) => {
      return isLocationFieldBlacklisted(fieldId, location);
    },
);

/**
 * Helps to determine which bubble to render in the status column
 * @param locationId
 */
export const selectStatusViewModel = (locationId: number) => createSelector({
  hasBlacklistedFields: selectHasBlacklistedFields(locationId),
  hasOpenDifferences: selectHasOpenDifferences(locationId),
  hasIgnoredDifferences: selectHasIgnoredDifferences(locationId),
  location: selectLocation(locationId),
});


/***************************************************************************************************
 * LOCATION TRANSLATION
 **************************************************************************************************/

// internal use only
export const addressTranslationsSelectors = addressTranslationsAdapter.getSelectors();
export const selectAddressTranslationsState = createSelector(state, (state) => state.addressTranslations);

export const selectAddressTranslations = createSelector(selectAddressTranslationsState, addressTranslationsSelectors.selectAll);
export const selectAddressTranslationsDictionary = createSelector(selectAddressTranslationsState, addressTranslationsSelectors.selectEntities);
export const selectAddressTranslation = (id) => createSelector(selectAddressTranslationsDictionary,
    (addressTranslations) => addressTranslations[id]);


/***************************************************************************************************
 *                                COLUMN NOTES SELECTORS                                           *
 **************************************************************************************************/

export const columnNotesSelectors = columnNotesAdapter.getSelectors();

export const selectColumnNotesState = createSelector(state, (locationList) =>
    locationList.columnNotes);

export const selectColumnNotesDictionary = createSelector(selectColumnNotesState, columnNotesSelectors.selectEntities); // dictionary

export const selectColumnNotes = createSelector(selectColumnNotesState, columnNotesSelectors.selectAll); // array

export const selectColumnNote = (columnName: string) => createSelector(selectColumnNotesDictionary, (columnNotesDictionary) => {
  if (columnNotesDictionary[columnName]) {
    return columnNotesDictionary[columnName];
    // if a note has been deleted return en 'empty' note object
  } else return { columnName: columnName, loading: false, noteColor: NoteColor.BLACK };
});


/***************************************************************************************************
 *                              DATA CONNECTION FUNCTIONALITY
 **************************************************************************************************/


export const selectActiveDataConnections = createSelector(state, (locationList) => {
  return locationList.activeDataConnections;
});

/**
 * Determines 'active data connection' status for a give fieldId in location list component
 * @param fieldId
 */
export const selectHasActiveConnection = (fieldId: string) => createSelector(
    selectActiveDataConnections,
    (activeDataConnections) => {
      // as an alternative fieldId might be returned from BE, convert it to primary fieldId, so we
      // can later check if one or the other is present in active connections fieldId array.
      fieldId = getPrimaryFieldId(fieldId);

      switch (fieldId) {
        case 'businessHours':
          return BUSINESS_HOURS_SUBFIELDS.every(fieldId => activeDataConnections.includes(fieldId));
        case 'coordinateImport':
          return [fieldId + '.latitude', fieldId + '.longitude']
              .every(composedFieldId => activeDataConnections.includes(composedFieldId));
        default:
          return activeDataConnections.includes(fieldId) ||
              // addresses a bug? where BE sometimes returns an alternative fieldID
              activeDataConnections.includes(ALTERNATIVE_FIELD_IDS[fieldId]);
      }
    });

/**
 * Gets complex data connection status for GBP fields that do not map directly to BLS fields,
 * but rather consists of several individual BLS fields - if not all subfields have a data src,
 * the GBP field is considered partially active. Relevant for Differences component
 * @param fieldId
 */
export const selectComplexDataConnectionStatus = (fieldId: string) => createSelector(
    selectActiveDataConnections,
    (activeDataConnections): DataSourceStatus | undefined => {
      if (!isComplexDataConnectionField(fieldId)) {
        return;
      }

      const everyDataSrcSubfieldHasActiveConnection = COMPLEX_DATA_CONNECTION_FIELDS[fieldId]
          .every(dataSrcSubfieldId => activeDataConnections.includes(dataSrcSubfieldId) ||
              activeDataConnections.includes(ALTERNATIVE_FIELD_IDS[dataSrcSubfieldId]));

      const someDataSrcSubfieldsHaveActiveConnection = COMPLEX_DATA_CONNECTION_FIELDS[fieldId]
          .some(dataSrcSubfieldId => activeDataConnections.includes(dataSrcSubfieldId) ||
              activeDataConnections.includes(ALTERNATIVE_FIELD_IDS[dataSrcSubfieldId]));


      return everyDataSrcSubfieldHasActiveConnection ? DataSourceStatus.ACTIVE :
          someDataSrcSubfieldsHaveActiveConnection ? DataSourceStatus.PARTIALLY_ACTIVE :
              DataSourceStatus.INACTIVE;
    });

/**
 * Used for determining whether custom svg (partial connection) or Material icon (active/inactive)
 * will be used. In case of an active/inactive connection, this selector is also used to determine
 * the necessary CSS class (filled or outline)
 * @param fieldId
 */
export const selectDataSourceIconType = (fieldId: string) => createSelector(
    selectHasActiveConnection(fieldId),
    selectComplexDataConnectionStatus(fieldId),
    (hasActiveConnection, complexDataConnectionStatus) => {

      // Differences-specific
      if (isComplexDataConnectionField(fieldId)) {
        return complexDataConnectionStatus.toLowerCase() as Lowercase<DataSourceStatus>;
      }

      // Location list-specific
      return (hasActiveConnection
              ? DataSourceStatus.ACTIVE.toLowerCase()
              : DataSourceStatus.INACTIVE.toLowerCase()
      ) as Lowercase<DataSourceStatus>;
    });

export const selectFocusedRowId = createSelector(state,
    (state) => state.focusedRowId);

export const selectHoveredRowId = createSelector(state,
    (state) => state.hoveredRowId);

export const selectSelectedRowsIds = createSelector(state,
    (state) => state.selectedRowsIds);

export const selectAreRowsSelected = createSelector(selectSelectedRowsIds, (selectedRowsIds) => {
  return !!selectedRowsIds?.length;
});

/***************************************************************************************************
 * CC SYNC STATUS
 **************************************************************************************************/

export const selectCCSyncStatus = createSelector(selectCCSyncDetails, (ccSyncDetails) => {
  const now = new Date();
  // new Date(0) = start of UNIX epoch
  const startDate = new Date(ccSyncDetails?.startDate ? ccSyncDetails?.startDate : 0);
  const endDate = new Date(ccSyncDetails?.endDate ? ccSyncDetails?.endDate : 0);
  // -|------|------|------|------>
  // End   N-24h  Start    N      t
  if (isAfter(startDate, subHours(now, 24)) &&
      isAfter(startDate, endDate)) {
    return CCSyncStatus.PROCESSING;
    // -|------|------|------|------>
    // End   Start  N-24h    N      t
  } else if (isBefore(startDate, subHours(now, 24)) &&
      isAfter(startDate, endDate)) {
    return CCSyncStatus.FAILED;
    // -|------|------|------|------>
    // N-36h  Start  End     N      t
  } else if (isAfter(startDate, subHours(now, 36)) &&
      isBefore(startDate, endDate)) {
    return CCSyncStatus.FINISHED;
  } else return CCSyncStatus.STALE;
});

export const selectCCSyncStatusIcon = createSelector(selectCCSyncStatus,
    (ccSyncStatus) => {
      switch (ccSyncStatus) {
        case CCSyncStatus.FINISHED:
          return {
            svgName: 'cc_sync_finish',
            class: 'text-green-50',
          };
        case CCSyncStatus.FAILED:
          return {
            svgName: 'cc_sync_fail',
            class: 'text-red-50',
          };
        case CCSyncStatus.PROCESSING:
          return {
            svgName: 'cc_sync_process',
            class: 'text-blue-50',
          };
        default: // CCSyncStatus.STALE
          return {
            svgName: 'cc_sync',
            class: 'text-neutral-40',
          };
      }
    });

/***************************************************************************************************
 * LOADING & ONGOING ACTIONS
 **************************************************************************************************/

export const selectLoading = createSelector(state, (state) => state.loading);

export const selectValidationHistoryLoading = createSelector(state, (state) => state.validationHistoryLoading);

export const selectOngoingActions = createSelector(state, (state) => state.ongoingActions);

export const selectIsOngoingAction = (ongoingAction: OngoingActionsEnum) =>
    createSelector(selectOngoingActions, (actions) => actions.includes(ongoingAction));
/**
 * Determines the disabled state for action bar buttons
 * @param action check whether the action that is about to be run isn't already running -
 */
export const selectCanRunAction = (action: OngoingActionsEnum = null) => createSelector(
    selectSelectedRowsIds,
    selectIsOngoingAction(action),
    (selectSelectedRowsIds, actionOngoing) => {
      // these don't require selected locations - check only if action is not ongoing
      if (action && action === OngoingActionsEnum.EXPORTING || action === OngoingActionsEnum.IMPORTING) {
        return !actionOngoing;
      }

      // if no action has been provided = copy to clipboard had been pressed, check only whether
      // locations are selected, otherwise check selected locations + ongoing actions
      return !!selectSelectedRowsIds.length && (action ? !actionOngoing : true);
    });


/***************************************************************************************************
 * COMBINED SELECTORS
 **************************************************************************************************/

/**
 * Get location subset based on table selection (checkmark)
 */
export const selectSelectedLocations = createSelector(selectSelectedRowsIds,
    selectLocations,
    (selectedRowIds, locations) => locations.filter(l => selectedRowIds.includes(l.id)),
);

/**
 * Get focused location = data src for components that can display/edit only single location data
 */
export const selectFocusedLocation = createSelector(selectFocusedRowId,
    selectLocationsEntities, (focusedRowId, locations) => {
      return locations[focusedRowId];
    });

export const selectHoveredLocation = createSelector(selectHoveredRowId,
    selectLocationsEntities, (hoveredRowId, locations) => {
      return locations[hoveredRowId];
    });

/**
 * Get focused location ID
 * is the same as selectFocusedRowId - deprecate?
 */
export const selectFocusedLocationId = createSelector(selectFocusedLocation,
    (focusedLocation) => {
      return focusedLocation?.id;
    });

/**
 * Get location subset based on 'foundInMyBusiness' status
 */
export const selectSelectedLocationsFoundInMyBusiness = createSelector(selectSelectedLocations,
    (selectedLocations) => {
      return selectedLocations.filter(l => l.foundInMyBusiness);
    });

/**
 * Get certain location data as string (copy-to-clipboard functionality)
 */
export const selectSelectedLocationsAsString = createSelector(selectSelectedLocations,
    (selectLocations) => {
      let locationStringArray: string[] = [];
      selectLocations.forEach(location => {
        let locationString = [location.storeCode, location.businessName, location.country,
          location.city, location.postalCode, location.addressLine1]
            .map(entry => entry && entry.trim().length ? entry : '-').join(' / ');
        locationStringArray.push(locationString);
      });
      return locationStringArray.join('\n');
    });

/**
 * Determines if bulk edit location groups popup can be opened
 * @param location
 */
export const selectCanTransfer = (location?: Location) => createSelector(
    selectSelectedLocationsFoundInMyBusiness,
    selectIsOngoingAction(OngoingActionsEnum.UPDATE_GBP_GROUPS),
    (selectedLocationsFoundInMyBusiness, updatingGbpGroups) => {
      if (location) {
        return location.foundInMyBusiness && !updatingGbpGroups;
      } else return selectedLocationsFoundInMyBusiness.length && !updatingGbpGroups;
    });

/**
 * Determines if bulk edit location groups popup can be opened
 * @param location
 */
export const selectCanBulkAcceptDiffs = createSelector(
    selectSelectedLocations,
    selectIsOngoingAction(OngoingActionsEnum.BULK_ACCEPT_DIFFERENCES),
    (selectedLocations, updatingGbpGroups) => {
      if (updatingGbpGroups) {
        return false;
      }

      let canBulkAcceptDiffs: boolean = false;

      for (let location of selectedLocations) {
        let locationDifferencesMap = location.differences;
        let keys = Object.keys(locationDifferencesMap);

        for (let key of keys) {
          let importedLocationDelta = locationDifferencesMap[key];
          if (importedLocationDelta.ignored === false) {
            canBulkAcceptDiffs = true;
            break;
          }
        }
      }
      return canBulkAcceptDiffs;
    });

/**
 * View model for the row menu
 */
export const selectRowMenuViewModel = (location: Location) =>
    createSelector(selectCanTransfer(location),
        selectOngoingActions,
        (canTransfer, ongoingActions) => {
          return {
            canTransfer: canTransfer,
            canUpdateInGbp: !ongoingActions.includes(OngoingActionsEnum.UPDATE_IN_GBP),
            canRefreshGbp: !ongoingActions.includes(OngoingActionsEnum.GET_UPDATES_FROM_GBP),
            canArchive: !ongoingActions.includes(OngoingActionsEnum.ARCHIVING),
            canBlacklist: !ongoingActions.includes(OngoingActionsEnum.BLACKLISTING),
            canSyncReviews: !ongoingActions.includes(OngoingActionsEnum.SYNC_REVIEWS) &&
                location.foundInMyBusiness,
            canCreateGbpConnection: canCreateGbpConnection(location, ongoingActions),
            canRemoveGbpConnection: !ongoingActions.includes(OngoingActionsEnum.REMOVE_GBP_CONNECTION) &&
                location.foundInMyBusiness,
            canIgnoreLastUpdateError: location.lastUpdateErrorMessage && location.foundInMyBusiness,
          };
        });